import { SCModal, SCToast } from '@lib/sc-eone-ui';

export class UserCancelledError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'UserCancelledError';
  }
}

/**
 * Error codes for which the app shows a modal (user must click OK to dismiss)
 * instead of an auto-dismiss toast. Add backend error codes here when the message
 * must be read and acknowledged.
 */
export const errorCodesShowAsModal = new Set<string>([
  'Process.Save.BusinessError',
  'DataSource.Save.BusinessError',
  'Connector.Save.BusinessError',
]);

/** Returns true if this error should be shown as a modal (confirm-to-dismiss) instead of toast. */
export function shouldShowErrorAsModal(err: unknown): boolean {
  const code = err?.['errorCode'] ?? err?.['code'];
  return typeof code === 'string' && errorCodesShowAsModal.has(code);
}

export function toastError(
  err: unknown,
  options?: Omit<Parameters<typeof SCToast.error>[0], 'content'>,
) {
  if (!err) return;
  SCToast.error({
    duration: 1000 * 10, // default to enough duration
    ...options,
    content: getErrorMessage(err),
  });
}

export function modalError(
  err: unknown,
  options?: Omit<Parameters<typeof SCModal.alert>[0], 'Message'>,
) {
  if (!err) return;
  return SCModal.alert({
    Title: 'Error',
    ...options,
    Message: getErrorMessage(err),
  });
}

// Note: type of error thrown can be unknown
export function getErrorMessage(err: unknown): string {
  if (!err) return '';
  // Prefer backend message from API response (EOneRequestError / Axios)
  const fromResponse =
    err?.['response']?.['data']?.errorMessage ?? err?.['response']?.['data']?.error;
  if (fromResponse != null && typeof fromResponse === 'string') return fromResponse;
  return String(err?.['message'] ?? err?.['errorMessage'] ?? err);
}
